/**
 * Shared contact-field validation for the booking widget.
 *
 * Single source of truth for the name / phone / email rules used on the
 * appointment review (confirm) screen AND the waiting-list join panel, so
 * the two can never diverge. Rules match the original inline confirm-screen
 * validation: name required; phone required and must start with the country
 * code ("+"); email optional but must look like an address.
 *
 * Validators return a stable error KEY (or null). Callers render the message
 * via `contactErrorMessage(key, t)` so translation stays at the call site.
 */
import { isValidPhoneNumber } from "libphonenumber-js";

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type ContactErrorKey =
  | "required"
  | "phone_country_code"
  | "phone_invalid"
  | "email_invalid";

export function validateName(name: string | null | undefined): ContactErrorKey | null {
  return name && name.trim() ? null : "required";
}

export function validatePhone(phone: string | null | undefined): ContactErrorKey | null {
  const p = (phone ?? "").trim();
  if (!p) return "required";
  if (!p.startsWith("+")) return "phone_country_code";
  // Per-country length + prefix (libphonenumber): catches numbers typed a
  // digit short — the same rule the backend enforces (app/utils/phone
  // .phone_is_valid), so the user is guided BEFORE submit instead of by a 400.
  if (!isValidPhoneNumber(p)) return "phone_invalid";
  return null;
}

/** Email is optional: empty is valid; if present it must match EMAIL_REGEX. */
export function validateOptionalEmail(email: string | null | undefined): ContactErrorKey | null {
  const e = (email ?? "").trim();
  if (e && !EMAIL_REGEX.test(e)) return "email_invalid";
  return null;
}

/**
 * Phone is optional: empty is valid; if present it must carry the country code.
 * For screens where the phone itself is mandatory, use `validatePhone`.
 */
export function validateOptionalPhone(phone: string | null | undefined): ContactErrorKey | null {
  const p = (phone ?? "").trim();
  if (p && !p.startsWith("+")) return "phone_country_code";
  return null;
}

/**
 * The example country code shown in the phone hint, derived from the company
 * settings `phone_code` (digits only, e.g. "420" → "+420"). Falls back to
 * "+44" when the salon has no phone_code configured.
 */
export function phoneCodeExample(phoneCode: string | null | undefined): string {
  const digits = (phoneCode ?? "").trim().replace(/^\+/, "");
  return digits ? `+${digits}` : "+44";
}

/** The phone input's hint in the salon's own country code, e.g. "+7 (__) ___-__-__" —
 *  every phone field of the widget (booking, sign-in, sign-up, review) shows the same. */
export function phonePlaceholder(phoneCode: string | null | undefined): string {
  return `${phoneCodeExample(phoneCode)} (__) ___-__-__`;
}

type Translate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

export function contactErrorMessage(
  key: ContactErrorKey,
  t: Translate,
  opts?: { phoneExample?: string },
): string {
  switch (key) {
    case "required":
      return t("widget.common.validation.required", "Please fill in this field");
    case "phone_country_code":
      // NEW key (the old `phone_country_code` is baked with a literal +375 in
      // the locale bundles, which i18next prefers over the code default —
      // that's why the artifact persisted). A fresh key falls back to this
      // English default until the translator regenerates the bundles.
      // {{code}} is interpolated from the salon's phone_code (default +44).
      return t(
        "widget.common.validation.phone_country_code_hint",
        "Phone number must start with the country code (for example, {{code}})",
        { code: opts?.phoneExample ?? "+44" },
      );
    case "phone_invalid":
      // Clipped/garbled numbers (a digit short of a real BY mobile, wrong
      // prefix, …) — mirrors the backend gate's message.
      return t(
        "widget.common.validation.phone_invalid",
        "This phone number looks incomplete — please check it",
      );
    case "email_invalid":
      return t("widget.common.validation.email_invalid", "Invalid email");
  }
}
